import React from 'react';
import { useCart, DELIVERY_OPTIONS, PAYMENT_OPTIONS } from '@/contexts/CartContext';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  ShoppingCart,
  Plus,
  Minus,
  Trash2,
  Truck,
  Store,
  Utensils,
  Banknote,
  CreditCard,
  QrCode,
  MessageCircle,
  AlertCircle,
  Clock,
  X,
  ShoppingBag,
} from 'lucide-react';

const iconMap = {
  Truck,
  Store,
  Utensils,
  Banknote,
  CreditCard,
  QrCode,
};

const WHATSAPP_NUMBER = '5588920005193';

export default function CartDrawer() {
  const {
    items,
    isCartOpen,
    setIsCartOpen,
    deliveryType,
    setDeliveryType,
    paymentMethod,
    setPaymentMethod,
    storeStatus,
    updateQuantity,
    removeItem,
    clearCart,
    getTotal,
    getTotalItems,
    generateWhatsAppMessage,
  } = useCart();

  const total = getTotal();
  const totalItems = getTotalItems();

  const handleSendOrder = () => {
    const message = generateWhatsAppMessage();
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${message}`, '_blank');
  };

  return (
    <Sheet open={isCartOpen} onOpenChange={setIsCartOpen}>
      <SheetContent 
        side="right" 
        className="w-full sm:w-[420px] bg-pizzaria-bg border-white/10 p-0 flex flex-col"
      >
        {/* Header */}
        <SheetHeader className="px-4 sm:px-6 py-4 border-b border-white/10 flex-shrink-0">
          <div className="flex items-center justify-between">
            <SheetTitle className="font-heading text-xl sm:text-2xl text-white uppercase flex items-center gap-2">
              <ShoppingCart className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
              Carrinho
              {totalItems > 0 && (
                <Badge className="bg-primary text-white ml-2 text-xs">
                  {totalItems}
                </Badge>
              )}
            </SheetTitle>
          </div>
        </SheetHeader>

        {/* Store Status Alert */}
        {!storeStatus.isOpen && (
          <div className="mx-4 sm:mx-6 mt-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-yellow-500 font-medium text-sm">Pizzaria Fechada</p>
              <p className="text-yellow-500/80 text-xs mt-0.5">
                {storeStatus.message}. Seu pedido será enviado, mas o atendimento retorna no próximo horário.
              </p>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {items.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
              <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-pizzaria-paper flex items-center justify-center mb-4">
                <ShoppingBag className="w-10 h-10 sm:w-12 sm:h-12 text-pizzaria-text-muted" />
              </div>
              <h3 className="font-heading text-lg sm:text-xl text-white uppercase mb-2">Carrinho Vazio</h3>
              <p className="text-pizzaria-text-secondary text-sm">
                Adicione deliciosas pizzas ao seu pedido!
              </p>
              <Button
                onClick={() => setIsCartOpen(false)}
                className="mt-6 bg-primary hover:bg-primary/90 text-white font-heading uppercase"
              >
                Ver Cardápio
              </Button>
            </div>
          ) : (
            <>
              {/* Cart Items */}
              <ScrollArea className="flex-1 px-4 sm:px-6">
                <div className="py-4 space-y-3">
                  {items.map((item) => (
                    <div
                      key={item.id}
                      className="bg-pizzaria-paper rounded-xl p-3 sm:p-4 border border-white/5"
                    >
                      <div className="flex gap-3">
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg object-cover flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex justify-between items-start gap-2">
                            <h4 className="font-heading text-sm sm:text-base text-white uppercase truncate">
                              {item.name}
                            </h4>
                            <button
                              onClick={() => removeItem(item.id)}
                              className="text-pizzaria-text-muted hover:text-red-500 transition-colors p-1 flex-shrink-0"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                          <p className="text-primary font-semibold text-sm sm:text-base mt-1">
                            {item.price}
                          </p>
                          {/* Quantity Controls */}
                          <div className="flex items-center justify-between mt-2">
                            <div className="flex items-center gap-1 sm:gap-2">
                              <button
                                onClick={() => updateQuantity(item.id, item.quantity - 1)}
                                className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-pizzaria-subtle flex items-center justify-center text-white hover:bg-primary transition-colors"
                              >
                                <Minus className="w-3 h-3 sm:w-4 sm:h-4" />
                              </button>
                              <span className="font-heading text-white w-6 sm:w-8 text-center text-sm sm:text-base">
                                {item.quantity}
                              </span>
                              <button
                                onClick={() => updateQuantity(item.id, item.quantity + 1)}
                                className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-pizzaria-subtle flex items-center justify-center text-white hover:bg-primary transition-colors"
                              >
                                <Plus className="w-3 h-3 sm:w-4 sm:h-4" />
                              </button>
                            </div>
                            <span className="text-pizzaria-text-secondary text-xs sm:text-sm">
                              Subtotal: R$ {(parseFloat(item.price.replace('R$', '').replace(',', '.').trim()) * item.quantity).toFixed(2).replace('.', ',')}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Delivery Options */}
                <div className="py-4 border-t border-white/10">
                  <h4 className="font-heading text-sm uppercase text-pizzaria-text-secondary mb-3 flex items-center gap-2">
                    <Truck className="w-4 h-4" />
                    Tipo de Entrega
                  </h4>
                  <div className="grid grid-cols-3 gap-2">
                    {DELIVERY_OPTIONS.map((option) => {
                      const Icon = iconMap[option.icon];
                      return (
                        <button
                          key={option.id}
                          onClick={() => setDeliveryType(option.id)}
                          className={`p-2 sm:p-3 rounded-xl border transition-all duration-200 flex flex-col items-center text-center ${
                            deliveryType === option.id
                              ? 'bg-primary/20 border-primary text-primary'
                              : 'bg-pizzaria-paper border-white/10 text-pizzaria-text-secondary hover:border-white/30'
                          }`}
                        >
                          <Icon className="w-4 h-4 sm:w-5 sm:h-5 mb-1" />
                          <span className="font-heading text-[10px] sm:text-xs uppercase leading-tight">
                            {option.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Payment Options */}
                <div className="py-4 border-t border-white/10">
                  <h4 className="font-heading text-sm uppercase text-pizzaria-text-secondary mb-3 flex items-center gap-2">
                    <CreditCard className="w-4 h-4" />
                    Forma de Pagamento
                  </h4>
                  <div className="grid grid-cols-2 gap-2">
                    {PAYMENT_OPTIONS.map((option) => {
                      const Icon = iconMap[option.icon];
                      return (
                        <button
                          key={option.id}
                          onClick={() => setPaymentMethod(option.id)}
                          className={`p-2 sm:p-3 rounded-xl border transition-all duration-200 flex items-center gap-2 ${
                            paymentMethod === option.id
                              ? 'bg-primary/20 border-primary text-primary'
                              : 'bg-pizzaria-paper border-white/10 text-pizzaria-text-secondary hover:border-white/30'
                          }`}
                        >
                          <Icon className="w-4 h-4 flex-shrink-0" />
                          <span className="font-heading text-[10px] sm:text-xs uppercase">
                            {option.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="pb-4" />
              </ScrollArea>

              {/* Footer */}
              <div className="flex-shrink-0 border-t border-white/10 bg-pizzaria-paper px-4 sm:px-6 py-4">
                {/* Total */}
                <div className="flex justify-between items-center mb-4">
                  <span className="text-pizzaria-text-secondary">Subtotal:</span>
                  <span className="font-heading text-xl sm:text-2xl text-white">
                    R$ {total.toFixed(2).replace('.', ',')}
                  </span>
                </div>

                {/* Actions */}
                <div className="space-y-2">
                  <Button
                    onClick={handleSendOrder}
                    className="w-full bg-green-600 hover:bg-green-700 text-white font-heading uppercase py-5 sm:py-6 text-sm sm:text-base"
                  >
                    <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                    Enviar Pedido pelo WhatsApp
                  </Button>
                  <Button
                    onClick={clearCart}
                    variant="outline"
                    className="w-full border-white/20 text-pizzaria-text-secondary hover:bg-white/5 font-heading uppercase text-xs sm:text-sm"
                  >
                    <Trash2 className="w-3 h-3 sm:w-4 sm:h-4 mr-2" />
                    Limpar Carrinho
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
